import { type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDate, localized } from '@rental/i18n'
import { Button, Field } from '@rental/ui'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { CalendarCog, CalendarX } from 'lucide-react'
import { useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { addDays } from '../board'
import { DateStepper } from '../components/DateStepper'
import { EmptyState } from '../components/EmptyState'
import { LookupProblem } from '../components/LookupProblem'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { Fetching } from '../components/Results'
import { Guard, LoadState } from '../components/states'
import { StaffSelect } from '../components/ui'
import { useLocale } from '../i18n'
import { type AppliedBulk, BulkDialog } from '../prices/BulkDialog'
import { type AskedDay, DayDialog } from '../prices/DayDialog'
import { PRICES_DEFAULT_DAYS, readPricesDays, rememberPricesDays } from '../prices/days'
import { PricesBoard } from '../prices/PricesBoard'
import { problemOf } from '../problems'
import { useRomeToday } from '../rome-today'
import { DaysControl } from './Timeline'

const route = getRouteApi('/prezzi')

type LocalizedText = ApiSchemas['StaffPricesCalendar']['categories'][number]['name']

/** The grid's query, by the days and the category it shows; every write reads it again. */
export const PRICES_QUERY_KEY = ['prices'] as const

/**
 * W2-S S1: Prezzi, the rate calendar, for whoever holds prices.manage (the
 * Owner by default). Anyone else who types its address reads the refusal
 * under its title, and nothing is asked of the server for it.
 */
export function PricesScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'prices.manage')} title={m.prices.title}>
      <Prices />
    </Guard>
  )
}

type Outcome = { key: number; text: string }

/**
 * The categories as rows, the days as columns, as the Planning lays them out:
 * each category on its pane, its days a region that scrolls sideways under its
 * name, every category's days at once. A day from today on opens its dialog;
 * many days change at once through the bulk dialog, previewed, then applied
 * with the Owner's code. The UI decides no price: it shows the server's and
 * sends what is typed.
 */
function Prices() {
  const { locale, messages: m } = useLocale()
  const words = m.prices
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const queryClient = useQueryClient()
  const today = useRomeToday()
  const from = search.from ?? today
  // The address names the span; without one, the span this device last showed here.
  const [remembered] = useState(() => readPricesDays())
  const days = search.days ?? remembered ?? PRICES_DEFAULT_DAYS
  const calendar = useQuery({
    queryKey: [...PRICES_QUERY_KEY, from, days, search.category ?? null],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/prices', {
            params: {
              query: {
                from,
                days,
                ...(search.category ? { categoryId: search.category } : {}),
              },
            },
          }),
        ),
      ),
    placeholderData: keepPreviousData,
  })
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => load(call(api.GET('/v1/categories'))),
    staleTime: Number.POSITIVE_INFINITY,
  })
  const data = calendar.data
  const fetching = calendar.isPlaceholderData
  const categorySelect = useRef<HTMLSelectElement>(null)
  const known = categories.data?.items.some((category) => category.id === search.category)
  const dayDialog = useRef<HTMLDialogElement>(null)
  const bulkDialog = useRef<HTMLDialogElement>(null)
  const [asked, setAsked] = useState<AskedDay | null>(null)
  const [bulkOpened, setBulkOpened] = useState(0)
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const go = (next: { from?: string; days?: number; category?: string | undefined }) => {
    if (next.days !== undefined) rememberPricesDays(next.days)
    void navigate({ to: '/prezzi', search: { ...search, ...next } })
  }
  const readAgain = () => queryClient.invalidateQueries({ queryKey: PRICES_QUERY_KEY })
  const said = (text: string) => setOutcome((last) => ({ key: (last?.key ?? 0) + 1, text }))

  // Every category the fleet has, for the bulk change; the grid's own while the list is not in.
  const allCategories: readonly { id: string; name: LocalizedText }[] =
    categories.data?.items ?? data?.categories ?? []

  return (
    <>
      <PageHeader
        title={words.title}
        actions={
          <Button
            variant="staff"
            disabled={!data}
            onClick={() => setBulkOpened((count) => count + 1)}
          >
            <CalendarCog aria-hidden="true" className="size-4" />
            {words.bulk}
          </Button>
        }
      >
        <div className="flex flex-col gap-4 pt-2">
          <p className="text-sm text-text-muted">{words.intro}</p>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <DateStepper
              label={words.from}
              value={from}
              today={today}
              step={days}
              onChange={(date) => go({ from: date })}
              words={{ earlier: words.earlier, later: words.later, today: words.today }}
            />
            <div className="flex flex-wrap items-end gap-3">
              <DaysControl days={days} onChange={(value) => go({ days: value })} />
              <Field label={words.category}>
                {(control) => (
                  <StaffSelect
                    {...control}
                    ref={categorySelect}
                    value={search.category ?? ''}
                    onChange={(event) => go({ category: event.target.value || undefined })}
                  >
                    <option value="">{words.allCategories}</option>
                    {search.category && !known ? (
                      <option value={search.category}>
                        {categories.data ? words.categoryGone : m.app.loading}
                      </option>
                    ) : null}
                    {categories.data?.items.map((category) => (
                      <option key={category.id} value={category.id}>
                        {localized(category.name, locale)}
                      </option>
                    ))}
                  </StaffSelect>
                )}
              </Field>
            </div>
          </div>
          <LookupProblem
            problem={problemOf(categories.error)}
            what={m.vehicles.categoriesFailed}
            busy={categories.isFetching}
            retry={categories.refetch}
            target={categorySelect}
          />
          {fetching ? <Fetching /> : null}
        </div>
      </PageHeader>

      {outcome ? <OutcomeNotice key={outcome.key}>{outcome.text}</OutcomeNotice> : null}

      <LoadState data={data} problem={problemOf(calendar.error)}>
        {(data) =>
          data.categories.length === 0 ? (
            search.category ? (
              <EmptyState icon={CalendarX} title={words.categoryGone}>
                {words.categoryGoneHint}
              </EmptyState>
            ) : (
              <EmptyState icon={CalendarX} title={words.noCategories}>
                {m.vehicles.fills}
              </EmptyState>
            )
          ) : (
            <PricesBoard
              // A new span or filter starts every category's days at its first day.
              key={`${data.from}:${data.days}:${search.category ?? ''}`}
              calendar={data}
              fetching={fetching}
              onOpen={(day) => setAsked(day)}
            />
          )
        }
      </LoadState>

      <DayDialog
        dialogRef={dayDialog}
        asked={asked}
        onSaved={(day) => {
          said(
            format(words.saved, {
              category: day.categoryName,
              date: formatDate(locale, day.day),
            }),
          )
          void readAgain()
        }}
        onConflict={() => void readAgain()}
      />
      {data ? (
        <BulkDialog
          dialogRef={bulkDialog}
          opened={bulkOpened}
          categories={allCategories.map((category) => ({
            id: category.id,
            name: localized(category.name, locale),
          }))}
          today={data.today}
          from={data.from < data.today ? data.today : data.from}
          to={laterOf(addDays(data.from, data.days - 1), data.today)}
          onApplied={(applied: AppliedBulk) => {
            said(format(words.applied, applied))
            void readAgain()
          }}
          onStale={() => void readAgain()}
        />
      ) : null}
    </>
  )
}

const laterOf = (a: string, b: string) => (a < b ? b : a)
